/// <amd-module name="Wit/QueriesHub/ResultsView/ResultsViewComponent" />
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableArray, ObservableValue, ReadyableObservableArray, IReadyableObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IPerformanceScenarioPendingWork, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { publishEvent } from "VSS/Platform/Telemetry";
import { FixedSizeLimitsFormat } from "VSSUI/Components/Splitter/Splitter.Props";
import { Link } from "VSSUI/Link";
import { IListSelection, ListSelection } from "VSSUI/List";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Splitter, SplitterDirection } from "VSSUI/Splitter";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITreeRow, Tree } from "VSSUI/TreeEx";
import { FILTER_CHANGE_EVENT, Filter } from "VSSUI/Utilities/Filter";
import { QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IQueryService, getQueryResultItem } from "Wit/Common/QueryService/IQueryService";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import * as QueriesHubConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import { AssignedToFilterProvider } from "Wit/Filter/AssignedToFilterProvider";
import { FieldFilterProvider } from "Wit/Filter/FieldFilterProvider";
import { FilterManager, IFilterDataSource } from "Wit/Filter/FilterManager";
import { TagsFilterProvider } from "Wit/Filter/TagsFilterProvider";
import { SubstringTextFilterProvider, TextFilterProvider } from "Wit/Filter/TextFilterProvider";
import { IQueryDisplayColumn, IQueryResult, IQueryResultItem } from "Wit/QueriesHub/Common/Interfaces";
import { QueryUtils } from "Wit/QueriesHub/Common/Utils/QueryUtils";
import * as Resources from "Wit/QueriesHub/Resources";
import { WorkItemTrackingDataProvider } from "Wit/UI/Data";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { WorkItemFormView } from "Wit/WorkItemForm/WorkItemFormView";
import { ILink, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventData, IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemFormUnsavedChangesService } from "Wit/WorkItemModel/Services/WorkItemFormUnsavedChangesService";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { QueryHeader, SplitterOptions } from "../Common/Components/QueryHeader";
import { ImportWorkItemsPanel } from "./ImportWorkItemsPanel";
import { QueryFilterDataSource } from "./QueryFilterDataSource";
import { ResultsFilterBar } from "./ResultsFilterBar";
import { ResultsTree } from "./ResultsTree";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ISelectionRange } from "VSSUI/Utilities/Selection";

const maxSizeWorkItemsBatchToFetchForFiltering = 1000;

interface IResultsViewComponentProps {
    isShowMoreItemsSet?: ObservableValue<boolean>;
}

export class ResultsViewComponent extends VssComponent<IResultsViewComponentProps> {
    private filter: Filter;
    private filteredQueryResultItems: ReadyableObservableArray<IQueryResultItem>;
    private filteredQueryResultItemsMap: Map<number, IQueryResultItem>;
    private workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    private selection = new ListSelection(true);
    private lastFocusedRow = new ObservableValue<number>(-1);
    private focusedWorkItem = new ObservableValue<{ loadingWorkItem: boolean; workItem?: IWorkItem }>({ loadingWorkItem: false });
    private treeRef = React.createRef<Tree<IQueryResultItem>>();
    private filterBarVisible = new ObservableValue<boolean>(false);
    private splitterDirection = new ObservableValue<string>(SplitterOptions.Right);
    private splitterSize: number | undefined;
    private queryService: IQueryService;
    private shortcutService: IVssKeyboardShortcutService;
    private isImport: boolean = false;
    private columns: ObservableArray<IQueryDisplayColumn> | undefined;
    private queryType = new ObservableValue<QueryType>(QueryType.Flat);
    private unfilteredQueryType: QueryType | undefined;
    private sourceIds = new ObservableArray<number>([]);
    private targetIds = new ObservableArray<number>([]);
    private pendingWork: IPerformanceScenarioPendingWork | undefined;
    private workItemFormTabId: ObservableValue<string> = new ObservableValue("");
    private workItemEventService: IWorkItemEventService;
    private resultsDivRef = React.createRef<HTMLDivElement>();
    private isFiltering = new ObservableValue<boolean>(false);
    private filterManager: FilterManager;
    private dataSource: IFilterDataSource;
    private enableNewBoardsQueriesNewDesignFF: boolean;

    constructor(props: IResultsViewComponentProps, context: IVssComponentContext) {
        super(props, context);
        this.workItemTrackingDataProvider = new WorkItemTrackingDataProvider(this.context.pageContext);
        this.shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        this.queryService = this.context.pageContext.getService<IQueryService>("IQueryService");
        this.filteredQueryResultItems = this.queryService.filteredQueryResultItems;
        this.filteredQueryResultItemsMap = this.queryService.filteredQueryResultItemsMap;

        this.filter = new Filter();

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues = historyService.getState();
        if (routeValues["importData"] === "csv") {
            this.isImport = true;
            this.columns = new ObservableArray<IQueryDisplayColumn>();
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const splitterDirectionSetting: { [key: string]: string } | undefined = settingsService.getEntry(
            QueriesHubConstants.SplitterDirectionKey,
            SettingsUserScope.Me
        );
        const splitterSizeSettings: { [key: string]: string } | undefined = settingsService.getEntry(
            QueriesHubConstants.WorkItemFormSplitterSize,
            SettingsUserScope.Me
        );

        if (splitterDirectionSetting && splitterDirectionSetting[QueriesHubConstants.SettingsKey]) {
            this.splitterDirection.value = splitterDirectionSetting[QueriesHubConstants.SettingsKey];
        }

        if (splitterSizeSettings && splitterSizeSettings[QueriesHubConstants.SettingsKey]) {
            this.splitterSize = Number(splitterSizeSettings[QueriesHubConstants.SettingsKey]) ?? undefined;
        }

        if (this.isImport) {
            this.workItemEventService = this.context.pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        }

        this.dataSource = new QueryFilterDataSource(this.context.pageContext, this.columns, this.queryService.queryResultItems, () => this.queryService.queryResultItemsMap);
        this.filterManager = new FilterManager(this.dataSource);
        this.filterManager.activate(this.context.pageContext);
        this.filterManager.registerFilterProvider(TextFilterProvider.PROVIDER_TYPE, new SubstringTextFilterProvider());
        this.filterManager.registerFilterProvider(CoreFieldRefNames.WorkItemType, new FieldFilterProvider(CoreFieldRefNames.WorkItemType));
        this.filterManager.registerFilterProvider(CoreFieldRefNames.State, new FieldFilterProvider(CoreFieldRefNames.State));
        this.filterManager.registerFilterProvider(CoreFieldRefNames.Tags, new TagsFilterProvider());

        const pageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();
        pageData && this.filterManager.registerFilterProvider(CoreFieldRefNames.AssignedTo, new AssignedToFilterProvider(pageData.user, this.context.pageContext));

        this.enableNewBoardsQueriesNewDesignFF = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign);
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this.filter.subscribe(this.onUpdateFilter, FILTER_CHANGE_EVENT);
        this.queryService.addResultListeners();
        this.registerShortcuts();
        if (this.isImport) {
            this.filteredQueryResultItemsMap = new Map<number, IQueryResultItem>();
            this.filteredQueryResultItems.value = [];
            this.filteredQueryResultItems.ready.value = true;
            this.onOpenImportPanel();
            this.workItemEventService.subscribe(
                ({ workItem }: IWorkItemEventData) => this.processWorkItemChanges(workItem),
                IWorkItemEventType.WorkItemUpdated
            );
            this.workItemEventService.subscribe(
                ({ workItem }: IWorkItemEventData) => this.processWorkItemSaved(workItem),
                IWorkItemEventType.WorkItemSaved
            );
        } else {
            if (!this.queryService.queryResult.value) {
                this.onRunQuery(/* temp_forInitialLoad */ true);
            } else {
                this.filteredQueryResultItemsMap = new Map(
                    this.queryService.queryResultItems.value.map(obj => [+obj.value[CoreFieldRefNames.Id], obj])
                );
                this.filteredQueryResultItems.value = [...this.queryService.queryResultItems.value];
                this.filteredQueryResultItems.ready.value = true;
                window.setTimeout(this.focusInitialRow, 0);
            }
        }

        this.onQueryResultUpdated();
    }

    public componentWillUnmount(): void {
        this.filter.unsubscribe(this.onUpdateFilter);
        this.unregisterShortcuts();
        this.queryService.removeResultListeners();
        if (this.isImport) {
            this.workItemEventService.unsubscribe(
                ({ workItem }: IWorkItemEventData) => this.processWorkItemChanges(workItem),
                IWorkItemEventType.WorkItemUpdated
            );
            this.workItemEventService.unsubscribe(
                ({ workItem }: IWorkItemEventData) => this.processWorkItemSaved(workItem),
                IWorkItemEventType.WorkItemSaved
            );
        }
    }

    private onUpdateFilter = () => {
        let isFiltered = false;
        for (const [key, value] of Object.entries(this.filter.getState())) {
            if (value?.value?.length) {
                isFiltered = true;
                break;
            }
        }
        this.isFiltering.value = isFiltered;
        if (this.isFiltering.value && !this.unfilteredQueryType) {
            this.unfilteredQueryType = this.queryType.value;
            this.queryType.value = QueryType.Flat;
        } else if (!this.isFiltering.value && this.unfilteredQueryType) {
            this.queryType.value = this.unfilteredQueryType;
            this.unfilteredQueryType = undefined;
        }
    };

    private onQueryResultUpdated = () => {
        if (!this.isImport) {
            this.queryType.value = this.queryService.getQueryType();
            this.sourceIds.value = this.queryService.queryResult.value?.data?.sourceIds || [];
            this.targetIds.value = this.queryService.queryResult.value?.data?.targetIds || [];
            if (this.queryService.queryResult.value?.data?.error) {
                reportError(this.context.pageContext, "query-service", this.queryService.queryResult.value?.data?.error);
            }
        }

        if (this.queryService.isRecycleBin && this.queryService.queryResult.value && !this.filteredQueryResultItems.ready.value) {
            this.filteredQueryResultItems.ready.value = true;
        }

        return false;
    };

    public render(): JSX.Element {
        const splitterDirectionIndex =
            this.splitterDirection.value === SplitterOptions.Bottom ? 0 : this.splitterDirection.value === SplitterOptions.Right ? 1 : 2;

        return (
            <PerformanceScenarioContext.Consumer>
                {scenarioProps => {
                    if (!this.pendingWork) {
                        this.pendingWork = scenarioProps.registerPendingWork(QueriesHubConstants.ResultsViewed);
                    }

                    return (
                        <Surface background={SurfaceBackground.neutral}>
                            <Page className="flex-grow flex-column">
                                <Observer targetIds={this.targetIds} isFiltering={this.isFiltering}>
                                    {(observerProps: { targetIds: number[]; isFiltering: boolean }) => {
                                        return (
                                            <QueryHeader
                                                selectedTabId={"results"}
                                                onRunQuery={this.onRunQuery}
                                                selection={this.selection}
                                                queryResultItems={this.filteredQueryResultItems}
                                                isDirty={this.queryService.isEditorDirty}
                                                isImport={this.isImport}
                                                lastFocusedRow={this.lastFocusedRow}
                                                splitterDirectionIndex={splitterDirectionIndex}
                                                onFocusNext={this.onFocusNext}
                                                onFocusPrevious={this.onFocusPrevious}
                                                onOpenImportPanel={this.onOpenImportPanel}
                                                onToggleFilterBar={this.onToggleFilterBar}
                                                onSplitterDirectionSelect={this.onSplitterDirectionSelect}
                                                onSaveItemsComplete={this.onSaveItemsComplete}
                                                workItemTrackingDataProvider={this.workItemTrackingDataProvider}
                                                isFiltering={observerProps.isFiltering}
                                                targetIdsCount={observerProps.targetIds.length}
                                            />
                                        );
                                    }}
                                </Observer>

                                {this.enableNewBoardsQueriesNewDesignFF ?
                                    (
                                        <Observer
                                            splitterDirection={this.splitterDirection}
                                            isQueryRunning={this.queryService.isQueryRunning}
                                            queryResultItemsReady={this.filteredQueryResultItems?.ready}
                                            selection={this.selection}
                                        >
                                            {(observerProps: {
                                                splitterDirection: string;
                                                queryResultItemsReady: boolean | undefined;
                                                isQueryRunning: boolean;
                                            }) => {
                                                if (observerProps.queryResultItemsReady) {
                                                    this.pendingWork?.complete();
                                                }
                                                const titleLinksToWorkItemForm = observerProps.splitterDirection === SplitterOptions.Off;
                                                const pageContent = (
                                                    <>
                                                        <ResultsFilterBar
                                                            filter={this.filter}
                                                            queryResultItems={this.queryService.queryResultItems}
                                                            getItemsMap={() => this.queryService.queryResultItemsMap}
                                                            getFilteredItemsMap={() => this.filteredQueryResultItemsMap}
                                                            filterBarVisible={this.filterBarVisible}
                                                            filteredQueryResultItems={this.filteredQueryResultItems}
                                                            onItemsFiltered={this.onItemsFiltered}
                                                            // this is needed to prevent issues with 'Save items' button
                                                            // when filter is enabled back.
                                                            isImport={this.isImport}
                                                            dataSource={this.dataSource}
                                                            filterManager={this.filterManager}
                                                            useNewResultsFilterBarDesign={this.enableNewBoardsQueriesNewDesignFF}
                                                        />
                                                        <Observer
                                                            queryResult={{
                                                                observableValue: this.queryService.queryResult,
                                                                filter: this.onQueryResultUpdated
                                                            }}
                                                        >
                                                            {() => (
                                                                <div
                                                                    ref={this.resultsDivRef}
                                                                    className="page-content page-content-top scroll-hidden full-height display-above-the-shim"
                                                                >
                                                                    <Observer
                                                                        filteredQueryResultItems={this.filteredQueryResultItems}
                                                                        queryResultItems={this.queryService.queryResultItems}
                                                                        fetchedItemsIds={this.queryService.fetchedItemsIds}
                                                                    >
                                                                        {({ fetchedItemsIds, queryResultItems }) => {
                                                                            const numberOfWorkItemsBeingFiltered = Object.keys(fetchedItemsIds).length;
                                                                            const numberOfWorkItemsToFetch = Math.min(
                                                                                this.queryService.totalNumberOfQueryResults - numberOfWorkItemsBeingFiltered,
                                                                                maxSizeWorkItemsBatchToFetchForFiltering
                                                                            );
                                                                            return (
                                                                                this.filter.hasChangesToReset() &&
                                                                                numberOfWorkItemsToFetch > 0 && (
                                                                                    <MessageBar
                                                                                        className="message-bar-small"
                                                                                        severity={MessageBarSeverity.Warning}
                                                                                    >
                                                                                        {format(
                                                                                            Resources.FilteringWorkItems,
                                                                                            numberOfWorkItemsBeingFiltered,
                                                                                            queryResultItems.length
                                                                                        )}
                                                                                        &nbsp;
                                                                                        <Link
                                                                                            className="text-ellipsis"
                                                                                            tooltipProps={{ overflowOnly: true }}
                                                                                            onClick={async () => {
                                                                                                this.filteredQueryResultItems.ready.value = false;
                                                                                                const notFetchedWorkItemIds =
                                                                                                    this.queryService.getNotFetchedWorkItemIds(
                                                                                                        this.targetIds.value,
                                                                                                        numberOfWorkItemsToFetch
                                                                                                    );
                                                                                                if (notFetchedWorkItemIds && notFetchedWorkItemIds.length > 0) {
                                                                                                    this.filterManager.dataUpdated();
                                                                                                }
                                                                                                await this.queryService.fetchWorkItems(notFetchedWorkItemIds);
                                                                                                this.onUpdateFilter();
                                                                                                this.filteredQueryResultItems.ready.value = true;
                                                                                            }}
                                                                                        >
                                                                                            {format(Resources.FilterNext, numberOfWorkItemsToFetch)}
                                                                                        </Link>
                                                                                    </MessageBar>
                                                                                )
                                                                            );
                                                                        }}
                                                                    </Observer>
                                                                    <ResultsTree
                                                                        rootRef={this.resultsDivRef}
                                                                        columns={this.columns}
                                                                        isImport={this.isImport}
                                                                        items={this.filteredQueryResultItems}
                                                                        getItemsMap={() => this.filteredQueryResultItemsMap}
                                                                        onChangeRecycleBin={this.onRunQuery}
                                                                        onDeleteItems={this.onDeleteItems}
                                                                        onModifyItems={this.onModifyItems}
                                                                        onFocus={this.onFocus}
                                                                        isShowMoreItemsSet={this.props.isShowMoreItemsSet}
                                                                        queryType={this.queryType}
                                                                        selection={this.selection}
                                                                        sourceIds={this.sourceIds}
                                                                        targetIds={this.targetIds}
                                                                        titleLinkOn={titleLinksToWorkItemForm}
                                                                        treeRef={this.treeRef}
                                                                        workItemTrackingDataProvider={this.workItemTrackingDataProvider}
                                                                        useNewResultsTreeDesign={this.enableNewBoardsQueriesNewDesignFF}
                                                                    />

                                                                    {this.filteredQueryResultItems && this.selection && (
                                                                        <div className="query-result-count-container-ff flex-column">
                                                                            <span className="query-result-count-ff">
                                                                                {
                                                                                    observerProps.isQueryRunning
                                                                                    ? Resources.Querying
                                                                                    : !observerProps.queryResultItemsReady
                                                                                    ? Resources.RunToSeeResults
                                                                                    : this.filteredQueryResultItems.length > 0 && this.selection.selectedCount > 0
                                                                                    ? format(Resources.WorkItemCount, this.getItemsCount(this.filteredQueryResultItems)) + ","
                                                                                    : this.filteredQueryResultItems.length > 0
                                                                                    ? format(Resources.WorkItemCount, this.getItemsCount(this.filteredQueryResultItems))
                                                                                    : Resources.NoQueryResults
                                                                                }

                                                                                {this.filteredQueryResultItems.ready.value && (
                                                                                    <span className="query-selection-count margin-left-4">{this.getLinksStatusString()}</span>
                                                                                )}
                                                                                {this.filteredQueryResultItems.ready.value && this.selection.selectedCount > 0 && (
                                                                                    <span className="query-selection-count margin-left-4">
                                                                                        {format(Resources.SelectedCount, this.selection.selectedCount)}
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    )}

                                                                </div>
                                                            )}
                                                        </Observer>
                                                    </>
                                                );

                                                return this.filteredQueryResultItems.length > 0
                                                    && (observerProps.splitterDirection === SplitterOptions.Bottom || observerProps.splitterDirection === SplitterOptions.Right) ? (
                                                    <ScreenSizeObserver>
                                                        {(screenSizeProps: { screenSize: ScreenSize}) => {
                                                            const smallScreen = screenSizeProps.screenSize === ScreenSize.xsmall || screenSizeProps.screenSize === ScreenSize.small;

                                                            return !smallScreen ? (
                                                                <Splitter
                                                                    className="scroll-hidden"
                                                                    farElementClassName="flex-column"
                                                                    nearElementClassName="flex-column"
                                                                    initialFixedSize={this.splitterSize}
                                                                    onFixedSizeChanged={this.onSplitterSizeChange}
                                                                    minFixedSize={0.2}
                                                                    maxFixedSize={0.8}
                                                                    fixedSizeLimitsFormat={FixedSizeLimitsFormat.Percentage}
                                                                    onRenderNearElement={() => pageContent}
                                                                    onRenderFarElement={() => (
                                                                        <Observer focusedWorkItem={this.focusedWorkItem} tabId={this.workItemFormTabId}>
                                                                            {({ focusedWorkItem, tabId }) =>
                                                                                focusedWorkItem.loadingWorkItem ? (
                                                                                    <Spinner className="flex-grow" size={SpinnerSize.large} />
                                                                                ) : focusedWorkItem.workItem ? (
                                                                                    <WorkItemFormView
                                                                                        className="themed full-height"
                                                                                        key={focusedWorkItem.workItem.id || focusedWorkItem.workItem.tempId}
                                                                                        selectedTabId={tabId}
                                                                                        ignoreUnsavedChanges={true}
                                                                                        workItem={focusedWorkItem.workItem}
                                                                                        onTabChange={this.onTabChange}
                                                                                        onDelete={() => {
                                                                                            const id = focusedWorkItem.workItem?.id;
                                                                                            if (id) {
                                                                                                this.onDeleteItems(undefined, [id]);
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                ) : null
                                                                            }
                                                                        </Observer>
                                                                    )}
                                                                    splitterDirection={
                                                                        observerProps.splitterDirection === SplitterOptions.Bottom
                                                                            ? SplitterDirection.Horizontal
                                                                            : SplitterDirection.Vertical
                                                                    }
                                                                />

                                                            ) : (pageContent);
                                                        }}

                                                    </ScreenSizeObserver>

                                                ) : (
                                                    pageContent
                                                );
                                            }}
                                        </Observer>
                                    ) :
                                    (
                                        <Observer splitterDirection={this.splitterDirection}
                                                  queryResultItemsReady={this.filteredQueryResultItems?.ready}>
                                            {(observerProps: { splitterDirection: string, queryResultItemsReady: boolean }) => {
                                            if (observerProps.queryResultItemsReady) {
                                                this.pendingWork?.complete();
                                            }
                                            const titleLinksToWorkItemForm = observerProps.splitterDirection === SplitterOptions.Off;
                                            const pageContent = (
                                                <>
                                                    {
                                                        <ResultsFilterBar
                                                            filter={this.filter}
                                                            queryResultItems={this.queryService.queryResultItems}
                                                            getItemsMap={() => this.queryService.queryResultItemsMap}
                                                            getFilteredItemsMap={() => this.filteredQueryResultItemsMap}
                                                            filterBarVisible={this.filterBarVisible}
                                                            filteredQueryResultItems={this.filteredQueryResultItems}
                                                            onItemsFiltered={this.onItemsFiltered}
                                                            // this is needed to prevent issues with 'Save items' button
                                                            // when filter is enabled back.
                                                            isImport={this.isImport}
                                                            dataSource={this.dataSource}
                                                            filterManager={this.filterManager}
                                                        />
                                                    }
                                                    <Observer
                                                        queryResult={{
                                                            observableValue: this.queryService.queryResult,
                                                            filter: this.onQueryResultUpdated
                                                        }}
                                                    >
                                                        {() => (
                                                            <div
                                                                ref={this.resultsDivRef}
                                                                className="page-content page-content-top scroll-hidden full-height display-above-the-shim"
                                                            >
                                                                <Observer
                                                                    filteredQueryResultItems={this.filteredQueryResultItems}
                                                                    queryResultItems={this.queryService.queryResultItems}
                                                                    fetchedItemsIds={this.queryService.fetchedItemsIds}
                                                                >
                                                                    {({ fetchedItemsIds, queryResultItems }) => {
                                                                        const numberOfWorkItemsBeingFiltered = Object.keys(fetchedItemsIds).length;
                                                                        const numberOfWorkItemsToFetch = Math.min(
                                                                            this.queryService.totalNumberOfQueryResults - numberOfWorkItemsBeingFiltered,
                                                                            maxSizeWorkItemsBatchToFetchForFiltering
                                                                        );
                                                                        return (
                                                                            this.filter.hasChangesToReset() &&
                                                                            numberOfWorkItemsToFetch > 0 && (
                                                                                <MessageBar
                                                                                    className="message-bar-small"
                                                                                    severity={MessageBarSeverity.Warning}
                                                                                >
                                                                                    {format(
                                                                                        Resources.FilteringWorkItems,
                                                                                        numberOfWorkItemsBeingFiltered,
                                                                                        queryResultItems.length
                                                                                    )}
                                                                                    &nbsp;
                                                                                    <Link
                                                                                        className="text-ellipsis"
                                                                                        tooltipProps={{ overflowOnly: true }}
                                                                                        onClick={async () => {
                                                                                            this.filteredQueryResultItems.ready.value = false;
                                                                                            const notFetchedWorkItemIds =
                                                                                                this.queryService.getNotFetchedWorkItemIds(
                                                                                                    this.targetIds.value,
                                                                                                    numberOfWorkItemsToFetch
                                                                                                );
                                                                                            if (notFetchedWorkItemIds && notFetchedWorkItemIds.length > 0) {
                                                                                                this.filterManager.dataUpdated();
                                                                                            }
                                                                                            await this.queryService.fetchWorkItems(notFetchedWorkItemIds);
                                                                                            this.onUpdateFilter();
                                                                                            this.filteredQueryResultItems.ready.value = true;
                                                                                        }}
                                                                                    >
                                                                                        {format(Resources.FilterNext, numberOfWorkItemsToFetch)}
                                                                                    </Link>
                                                                                </MessageBar>
                                                                            )
                                                                        );
                                                                    }}
                                                                </Observer>
                                                                <ResultsTree
                                                                    rootRef={this.resultsDivRef}
                                                                    columns={this.columns}
                                                                    isImport={this.isImport}
                                                                    items={this.filteredQueryResultItems}
                                                                    getItemsMap={() => this.filteredQueryResultItemsMap}
                                                                    onChangeRecycleBin={this.onRunQuery}
                                                                    onDeleteItems={this.onDeleteItems}
                                                                    onModifyItems={this.onModifyItems}
                                                                    onFocus={this.onFocus}
                                                                    isShowMoreItemsSet={this.props.isShowMoreItemsSet}
                                                                    queryType={this.queryType}
                                                                    selection={this.selection}
                                                                    sourceIds={this.sourceIds}
                                                                    targetIds={this.targetIds}
                                                                    titleLinkOn={titleLinksToWorkItemForm}
                                                                    treeRef={this.treeRef}
                                                                    workItemTrackingDataProvider={this.workItemTrackingDataProvider}
                                                                />
                                                            </div>
                                                        )}
                                                    </Observer>
                                                </>
                                            );
                                            return observerProps.splitterDirection === SplitterOptions.Bottom ||
                                                observerProps.splitterDirection === SplitterOptions.Right ? (
                                                <Splitter
                                                    className="scroll-hidden"
                                                    farElementClassName="flex-column"
                                                    nearElementClassName="flex-column"
                                                    initialFixedSize={this.splitterSize}
                                                    onFixedSizeChanged={this.onSplitterSizeChange}
                                                    minFixedSize={0.2}
                                                    maxFixedSize={0.8}
                                                    fixedSizeLimitsFormat={FixedSizeLimitsFormat.Percentage}
                                                    onRenderNearElement={() => pageContent}
                                                    onRenderFarElement={() => (
                                                        <Observer focusedWorkItem={this.focusedWorkItem} tabId={this.workItemFormTabId}>
                                                            {({ focusedWorkItem, tabId }) =>
                                                                focusedWorkItem.loadingWorkItem ? (
                                                                    <Spinner className="flex-grow" size={SpinnerSize.large} />
                                                                ) : focusedWorkItem.workItem ? (
                                                                    <WorkItemFormView
                                                                        className="themed full-height"
                                                                        key={focusedWorkItem.workItem.id || focusedWorkItem.workItem.tempId}
                                                                        selectedTabId={tabId}
                                                                        ignoreUnsavedChanges={true}
                                                                        workItem={focusedWorkItem.workItem}
                                                                        onTabChange={this.onTabChange}
                                                                        onDelete={() => {
                                                                            const id = focusedWorkItem.workItem?.id;
                                                                            if (id) {
                                                                                this.onDeleteItems(undefined, [id]);
                                                                            }
                                                                        }}
                                                                    />
                                                                ) : null
                                                            }
                                                        </Observer>
                                                    )}
                                                    splitterDirection={
                                                        observerProps.splitterDirection === SplitterOptions.Bottom
                                                            ? SplitterDirection.Horizontal
                                                            : SplitterDirection.Vertical
                                                    }
                                                />
                                            ) : (
                                                pageContent
                                            );
                                        }}
                                        </Observer>
                                    )
                                }
                            </Page>
                        </Surface>
                    );
                }}
            </PerformanceScenarioContext.Consumer>
        );
    }
    private onDeleteItems = (workItems?: INamedFieldDataDictionary[], workItemIds?: number[]) => {
        const idsSet = workItems ? new Set(workItems.map(workItem => workItem[CoreFieldRefNames.Id])) : new Set(workItemIds ?? []);
        this.queryService.removeWorkItems(idsSet);

        this.selection.clear();
    };

    private onModifyItems = (items: IWorkItem[]): void => {
        // re-render the form
        this.lastFocusedRow.value = this.lastFocusedRow.value;
    };

    private onImport = (queryResult: IQueryResult) => {
        if (!queryResult || !queryResult.payload.columns || !queryResult.payload.rows) {
            return;
        }
        this.queryService.clearData();

        this.filteredQueryResultItems.ready.value = false;
        const queryResultItems: IQueryResultItem[] = queryResult.payload.rows.map(r =>
            getQueryResultItem(queryResult.payload.columns as string[], r)
        );

        this.queryType.value = !!queryResult.isTreeQuery ? QueryType.Tree : QueryType.Flat;
        this.targetIds.value = queryResult.targetIds;
        this.sourceIds.value = queryResult.sourceIds;

        this.columns!.value = queryResult.columns;
        this.filteredQueryResultItemsMap = new Map(queryResultItems.map(obj => [+obj.value[CoreFieldRefNames.Id], obj]));
        this.filteredQueryResultItems.value = queryResultItems;

        if (!this.enableNewBoardsQueriesNewDesignFF) {
            this.filteredQueryResultItems.ready.value = true;
        }

        if (queryResult.workItems) {
            this.queryService.workItems.value = queryResult.workItems;

            // process work item valid/dirty state changes that we may have missed because query results weren't populated yet
            this.queryService.workItems.value.forEach(workItem => this.processWorkItemChanges(workItem));
            this.queryService.queryResultItemsMap = new Map(queryResultItems.map(obj => [+obj.value[CoreFieldRefNames.Id], obj]));
            this.queryService.queryResultItems.value = queryResultItems;
        }

        if (this.enableNewBoardsQueriesNewDesignFF) {
            this.filteredQueryResultItems.ready.value = true;
        }
    };

    private onOpenImportPanel = () => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => <ImportWorkItemsPanel onDismiss={onDismiss} onImportClick={this.onImport} />);
    };

    private onTabChange = (tabId: string) => {
        this.workItemFormTabId.value = tabId;
    };

    private onSplitterDirectionSelect = (value: string) => {
        this.splitterDirection.value = value;
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = {
            [`${QueriesHubConstants.SplitterDirectionKey}/${QueriesHubConstants.SettingsKey}`]: value
        };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    };

    private onSplitterSizeChange = (value: number) => {
        this.splitterSize = value;
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = {
            [`${QueriesHubConstants.WorkItemFormSplitterSize}/${QueriesHubConstants.SettingsKey}`]: value
        };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    };

    private registerShortcuts = (): void => {
        this.shortcutService.registerShortcut(Resources.ResultsTab, {
            combos: ["ctrl+a"],
            description: Resources.SelectAll,
            action: () => {
                this.onSelectAll();
            }
        });
    };

    private unregisterShortcuts = () => {
        this.shortcutService.removeShortcutGroup(Resources.ResultsTab);
    };

    private onSelectAll = () => {
        if (this.filteredQueryResultItems.value && this.filteredQueryResultItems.value.length > 0) {
            this.selection.select(0, this.filteredQueryResultItems.value.length);
            this.focusItem(this.filteredQueryResultItems.value[0], 0);
        }
    };

    private onFocus = (event: React.SyntheticEvent<HTMLElement>, listRow: ITreeRow<IQueryResultItem>) => {
        const { data, index } = listRow;
        this.focusItem(data.underlyingItem.data, index);
    };

    private focusItem = async (resultItem: IQueryResultItem, index: number) => {
        const itemId = Number(resultItem.value[CoreFieldRefNames.Id]);
        const isNewWorkItem = itemId < 0;
        if (!itemId) {
            return;
        }

        this.lastFocusedRow.value = index;
        let workItem = this.queryService.workItems.value.find(item => itemId === (isNewWorkItem ? item.tempId : item.id));
        if (!workItem && !isNewWorkItem) {
            const workItemModelService = this.context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            this.focusedWorkItem.value = { loadingWorkItem: true };
            const isDeleted = this.queryService.isRecycleBin;
            workItem = await workItemModelService.getWorkItem(itemId, { isDeleted, includeHistory: false });
            this.queryService.workItems.push(workItem);
        }

        if (workItem) {
            this.context.pageContext.getService<IWorkItemFormUnsavedChangesService>("IWorkItemFormUnsavedChangesService").register(workItem);
        }

        if (this.lastFocusedRow.value === index) {
            // don't update if selection changed while the work item was loading
            this.focusedWorkItem.value = { workItem, loadingWorkItem: false };
        }
    };

    private onFocusNext = () => {
        QueryUtils.onFocusNext(this.treeRef, this.lastFocusedRow, this.queryService.queryResultItems.length, this.selection);
    };

    private onFocusPrevious = () => {
        QueryUtils.onFocusPrevious(this.treeRef, this.lastFocusedRow, this.selection);
    };

    private onToggleFilterBar = () => {
        this.filterBarVisible.value = !this.filterBarVisible.value;
    };

    private onRunQuery = async (temp_forInitialLoad?: boolean) => {
        if (this.queryService.workItems.value.some(workItem => workItem.dirty.value)) {
            const unsavedChangesService =
                this.context.pageContext.getService<IWorkItemFormUnsavedChangesService>("IWorkItemFormUnsavedChangesService");
            if (!(await unsavedChangesService.showMessage())) {
                return;
            } else {
                // Reset changes if discarded (save triggers this too but there shouldn't be any dirty items)
                this.queryService.workItems.value.forEach(workItem => workItem.dirty.value && workItem.reset());
            }
        }

        this.filteredQueryResultItems.ready.value = false;
        const projectId = getTFSPageData(this.context.pageContext)!.project!.id;

        let wiql = "";
        if (!this.queryService.isEditorDirty.value && this.queryService.wiql.value) {
            wiql = this.queryService.wiql.value;
        } else {
            /*
             * TODO: #1888994 - there is a bug where if we load the results view with wiql specified in the url,
             * about one time in 20 we will run the default "all work items in this project" query instead of
             * using the specified wiql. This bug can be artificially reproduced by either setting isEditorDirty to false
             * or setting queryService.wiql to something falsy. It's not obvious which of these is actually the problem.
             * I made a change in queryService.initialize() assuming it's the latter, but in case it doesn't fix the
             * problem, this telemetry should be able to help us figure out whether something else is the problem.
             */
            if (temp_forInitialLoad) {
                publishEvent(this.context.pageContext, "WIT", "ResultsViewComponent_initialLoad_generateWIQL", {
                    url: window.location.toString(),
                    "queryService.isEditorDirty.value": this.queryService.isEditorDirty.value,
                    "queryService.wiql.value": this.queryService.wiql.value,
                    "queryService.queryId": this.queryService.queryId
                });
            }
            wiql = await this.queryService.generateWiql(projectId);
        }

        if (wiql && wiql.length !== 0) {
            this.focusedWorkItem.value = { loadingWorkItem: false };
            await this.queryService.executeQuery(projectId, wiql, this.props.isShowMoreItemsSet?.value);
            this.queryService.clearFetchedItems();
            this.selection.clear();
            this.lastFocusedRow.value = -1;

            //in case if User hit run query button with configured filter
            this.unfilteredQueryType = undefined;
            this.onUpdateFilter();

            this.filteredQueryResultItems.ready.value = true;
            this.focusInitialRow();
        }
    };

    private focusInitialRow = () => {
        QueryUtils.focusInitialRow(this.queryService, this.treeRef, this.lastFocusedRow, this.filteredQueryResultItems, this.selection);
    };

    private onSaveItemsComplete = (workItems: IWorkItem[]): void => {
        if (this.isImport) {
            const tryReplaceTempIds = (tempIds: number[]) => {
                return tempIds.map(oldIdValue => {
                    if (oldIdValue > 0) {
                        return oldIdValue;
                    }

                    const workItem = workItems.find(wi => wi.tempId === oldIdValue);

                    if (workItem) {
                        this.updateMapTempIdByOrdinaryId(workItem);

                        return workItem.id;
                    } else {
                        return oldIdValue;
                    }
                });
            };

            this.targetIds.value = tryReplaceTempIds(this.targetIds.value);
            this.sourceIds.value = tryReplaceTempIds(this.sourceIds.value);
        }

        workItems.forEach(wi => this.processWorkItemChanges(wi));
    };

    //ResultsTree uses targetIds value while rendering
    //so we need that map of workItems have same keys like value of targetIds
    private updateMapTempIdByOrdinaryId(workItem: IWorkItem) {
        const useQueryServiceAsSource = this.queryResultItemsAreSet();
        const queryResultItemsMap = this.getActualMap(useQueryServiceAsSource);
        const resultObservable = queryResultItemsMap.get(workItem.tempId);

        if (resultObservable && workItem.id > 0) {
            queryResultItemsMap.delete(workItem.tempId);
            queryResultItemsMap.set(workItem.id, resultObservable);

            if (useQueryServiceAsSource && this.filteredQueryResultItemsMap.has(workItem.tempId)) {
                this.filteredQueryResultItemsMap.delete(workItem.tempId);
                this.filteredQueryResultItemsMap.set(workItem.id, resultObservable);
            }
        }
    }

    private getActualMap(useQueryServiceAsSource: boolean) {
        return useQueryServiceAsSource ? this.queryService.queryResultItemsMap : this.filteredQueryResultItemsMap;
    }

    private queryResultItemsAreSet() {
        return this.queryService.queryResultItems.value.length > 0;
    }

    private processWorkItemSaved(workItem: IWorkItem): void {
        if (this.isImport && workItem.id > 0) {
            const linksPointedToThatWorkItem = this.getLinksPointedToThatWorkItem(workItem);
            if (linksPointedToThatWorkItem.length > 0) {
                linksPointedToThatWorkItem.forEach(link => (link.linkData.ID = workItem.id));
            }
        }
    }

    private getLinksPointedToThatWorkItem(workItem: IWorkItem): ILink[] {
        const result: ILink[] = [];

        for (const workItemWithLinks of this.queryService.workItems.value) {
            const links = workItemWithLinks
                .getLinks()
                ?.filter(addedLink => addedLink.isNew() && addedLink instanceof WorkItemLink && addedLink.linkData.ID === workItem.tempId);
            if (links) {
                result.push(...links);
            }
        }

        return result;
    }

    private processWorkItemChanges(workItem: IWorkItem): void {
        const useQueryServiceAsSource = this.queryResultItemsAreSet();
        const queryResultItemsMap = this.getActualMap(useQueryServiceAsSource);

        const stroredByTempId = queryResultItemsMap.has(workItem.tempId);
        const resultObservable = queryResultItemsMap.get(stroredByTempId ? workItem.tempId : workItem.id);

        if (!resultObservable) {
            return;
        }

        // process all fields that are being displayed, plus ID and State
        const result = resultObservable.value;
        const fieldsToProcess = new Set([CoreFieldRefNames.Id, CoreFieldRefNames.State, ...Object.keys(result)]);
        if (this.isImport) {
            fieldsToProcess.add(CoreFieldRefNames.TeamProject);
        }
        for (const fieldRefName of fieldsToProcess) {
            result[fieldRefName] = workItem.getFieldValueByName(fieldRefName);
        }

        result.isDirty = workItem.dirty.value;
        result.isInvalid = !workItem.isValid() || !!workItem.getError();

        // trigger a re-render of the row
        resultObservable.value = result;
    }

    /**
     * Update selection when filtered items are updated
     */
    private onItemsFiltered = (oldFilteredItems: IQueryResultItem[], newFilteredItems: IQueryResultItem[]): void => {
        if (!newFilteredItems.length || !this.treeRef.current) {
            return;
        }

        // update the selection to point to the same item if possible
        const selectedItemId = oldFilteredItems[this.lastFocusedRow.value]?.value[CoreFieldRefNames.Id];
        const index = newFilteredItems.findIndex(item => item.value[CoreFieldRefNames.Id] === selectedItemId);
        const indexToSelect = index > -1 ? index : 0;

        if (index === -1 && oldFilteredItems?.length && oldFilteredItems.length === newFilteredItems.length) {
            this.focusInitialRow();
        } else {
            const item = newFilteredItems[indexToSelect];
            this.focusItem(item, indexToSelect);
            this.selection.select(indexToSelect);
        }
    }

    private getItemsCount = (queryResultItems: IReadyableObservableArray<IQueryResultItem>): number => {
        if (this.isFiltering) {
            return queryResultItems.length;
        }
        return this.targetIds.length ?? queryResultItems.length;
    };

    private getLinksStatusString(): string | undefined {
        if (
            !this.filteredQueryResultItems?.length ||
            this.filteredQueryResultItems.length <= 0 ||
            this.isFiltering ||
            !(this.queryService.queryResult.value?.data?.isTreeQuery || this.queryService.queryResult.value?.data?.isLinkQuery)
        ) {
            return;
        }

        const linkedItemsCount = this.queryService.queryResult.value.data.sourceIds.filter(id => id > 0).length;
        const topLevelItemsCount = this.getItemsCount(this.queryService.queryResultItems) - linkedItemsCount;

        return format(Resources.QueryResultsTreeStatusTextFormat, topLevelItemsCount, linkedItemsCount);
    }
}

Components.add("query-results-view-component", ResultsViewComponent);
